// @flow
import { validateZipCode } from '../../utils/validations';

// The answers the legacy form requires before the recert can be completed (its validateComplete).
// The household answers, the second and the previous employer are not required there either.

const FULL_TIME_PREFIX = 'f_t';

const FULL_TIME_QUESTIONS = [
  'TANFAssistance',
  'jobTrainingProgram',
  'singleParentDependantChild',
  'marriedJointReturn',
  'fosterProgram',
];

const CURRENT_EMPLOYER_FIELDS = [
  'currentJobPosition',
  'currentJobAnnualSalary',
  'currentJobStartDate',
  'currentJobEmployerName',
  'currentJobEmployerPhone',
  'currentJobSupervisorName',
  'currentJobStreetAddress',
  'currentJobCity',
  'currentJobState',
  'currentJobZipCode',
];

const ZIP_CODE_FIELDS = [
  'currentJobZipCode',
  'secondJobZipCode',
  'previousJobZipCode',
];

type Options = { employedStatusId: ?string };

const isBlank = (value: mixed) =>
  value === undefined || value === null || value === '';

/** The fields that must be answered, given the answers so far. */
export const requiredFields = (
  values: Object,
  { employedStatusId }: Options,
): Array<string> => {
  const isStudent = values.isStudent === 'yes';
  const isFullTime =
    isStudent && String(values.studentType ?? '').startsWith(FULL_TIME_PREFIX);
  const isEmployed =
    Boolean(employedStatusId) && values.employmentStatus === employedStatusId;
  return [
    'isStudent',
    ...(isStudent ? ['studentType'] : []),
    ...(isFullTime ? FULL_TIME_QUESTIONS : []),
    'employmentStatus',
    'anticipateChangeEmployer',
    ...(isEmployed ? CURRENT_EMPLOYER_FIELDS : []),
  ];
};

export const invalidZipFields = (values: Object): Array<string> =>
  ZIP_CODE_FIELDS.filter(
    (field) =>
      !isBlank(values[field]) && !validateZipCode(String(values[field])),
  );

/** A field rule that requires the field only while the answers so far ask for it. */
export const requiredWhenAsked =
  (name: string, options: Options) =>
  ({ getValues }: { getValues: () => Object }): boolean =>
    requiredFields(getValues(), options).includes(name);

export const hasRequiredAnswers = (values: Object, options: Options): boolean =>
  // Without the statuses it cannot be told whether the current employer is required.
  Boolean(options.employedStatusId) &&
  requiredFields(values, options).every((field) => !isBlank(values[field])) &&
  invalidZipFields(values).length === 0;

export const isSignedCopyChecked = (value: mixed): boolean =>
  value === true || value === 'on';

/** Complete without an actual signature: the answers plus the signed-copy box. */
export const isRecertComplete = (values: Object, options: Options): boolean =>
  hasRequiredAnswers(values, options) &&
  isSignedCopyChecked(values.hasSignedCopy);
